import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import ScrollToTop from './ScroollToTop';

const ContactPage = () => {

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-gray-50 font-serif">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-gray-900 to-gray-700 text-white py-20">
          <div className="absolute inset-0 opacity-10">
            <img
              src="/assets/slide3.png"
              alt="Contact Background"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Get In Touch</h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Have questions about our furniture? We're here to help you create the perfect space.
            </p>
          </div>
        </section>

        {/* Contact Information Cards */}
        <section className="py-12 -mt-16 relative z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Phone Card */}
              <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500 rounded-full mb-4">
                  <Phone className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Call Us</h3>
                <p className="text-gray-600 text-sm mb-2">Mon-Fri: 9am - 6pm</p>
                <a href="tel:+12345678901" className="text-gray-800 hover:text-yellow-600 font-medium block">
                  +1 (234) 567-8901
                </a>
                <a href="tel:+12349875544" className="text-gray-800 hover:text-yellow-600 font-medium block">
                  +1 (234) 987-5544
                </a>
              </div>

              {/* Email Card */}
              <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500 rounded-full mb-4">
                  <Mail className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Email Us</h3>
                <p className="text-gray-600 text-sm mb-2">We'll respond within 24hrs</p>
                <a href="mailto:info@ripewood.in" className="text-gray-800 hover:text-yellow-600 font-medium block">
                  info@ripewood.in
                </a>
              </div>

              {/* Location Card */}
              <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500 rounded-full mb-4">
                  <MapPin className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Visit Us</h3>
                <p className="text-gray-600 text-sm mb-2">Showroom & Office</p>
                <p className="text-gray-800 font-medium">
                  121 Rock Street, 21 Avenue<br />
                  New York, NY 92103-00000
                </p>
              </div>

              {/* Hours Card */}
              <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500 rounded-full mb-4">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Business Hours</h3>
                <p className="text-gray-800 font-medium">
                  Mon - Fri: 9am - 6pm<br />
                  Sat: 10am - 4pm<br />
                  Sun: Closed
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="pt-6 pb-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Why Contact Us */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Contact Us?</h3>
                <ul className="space-y-6">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mt-1">
                      <span className="text-white text-base font-bold">✓</span>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-xl font-bold text-gray-900 mb-1">Expert Consultation</h4>
                      <p className="text-gray-600 text-base">Get personalized advice from our furniture specialists</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mt-1">
                      <span className="text-white text-base font-bold">✓</span>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-xl font-bold text-gray-900 mb-1">Custom Solutions</h4>
                      <p className="text-gray-600 text-base">Discuss custom furniture designs for your space</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mt-1">
                      <span className="text-white text-base font-bold">✓</span>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-xl font-bold text-gray-900 mb-1">Commercial Projects</h4>
                      <p className="text-gray-600 text-base">Bulk orders and commercial furniture solutions</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mt-1">
                      <span className="text-white text-base font-bold">✓</span>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-xl font-bold text-gray-900 mb-1">After-Sales Support</h4>
                      <p className="text-gray-600 text-base">Assistance with delivery, assembly, and maintenance</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* FAQ Quick Links */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-700 rounded-lg shadow-lg p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Quick Answers</h3>
                <p className="text-gray-300 mb-6 text-base leading-relaxed">
                  Looking for quick answers? Check out our frequently asked questions.
                </p>
                <ul className="space-y-4">
                  <li>
                    <Link to="/customize" className="text-yellow-400 no-underline flex items-center text-lg">
                      <span className="mr-3 text-xl">→</span> <span className="hover:underline">Customize Products</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/interior-solution" className="text-yellow-400 no-underline flex items-center text-lg">
                      <span className="mr-3 text-xl">→</span> <span className="hover:underline">Interior Solution</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/faq" className="text-yellow-400 no-underline flex items-center text-lg">
                      <span className="mr-3 text-xl">→</span> <span className="hover:underline">Shipping & Delivery</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/faq" className="text-yellow-400 no-underline flex items-center text-lg">
                      <span className="mr-3 text-xl">→</span> <span className="hover:underline">Product Care</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/faq" className="text-yellow-400 no-underline flex items-center text-lg">
                      <span className="mr-3 text-xl">→</span> <span className="hover:underline">Warranty Information</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/faq" className="text-yellow-400 no-underline flex items-center text-lg">
                      <span className="mr-3 text-xl">→</span> <span className="hover:underline">Returns & Exchanges</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="w-full bg-gray-100">
          <div className="w-full h-64 md:h-96 relative">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3684.0977326567845!2d88.43088631495804!3d22.572599985186823!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a0275a4e6f8b3e5%3A0x4b3b3b3b3b3b3b3b!2sSalt%20Lake%20Sector%205%2C%20Kolkata%2C%20West%20Bengal!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ 
                border: 0
              }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="RipeWood Location - Salt Lake Sector 5, Kolkata"
            ></iframe>
            <div className="absolute bottom-4 right-4 bg-white px-3 py-2 rounded shadow-md text-xs">
              <a 
                href="https://www.google.com/maps/search/?api=1&query=22.5726,88.4331" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 no-underline font-medium"
              >
                View on Google Maps →
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactPage;
